/**
 * Contains components and constants for the content area of the screen (not counting
 * the sidebar or site header).
 *
 * Key numbers:
 * - We desgin against a 12col grid, max 1040px width. Some screens take all 12 cols, others take less.
 * - 16px gutter between columns and outside the grid.
 * - On screens big enough to render the content + gutters with extra space, center the grid cols.
 * - The content area's width is the same as the screen width, unless the right sidebar is open.
 * - We use container queries to resize. So whether it's 1024px wide because the screen is 1024px wide,
 *   or because the right sidebar is open screenWidth-sidebarWidth == 1024px, the content area will look the same.
 *
 * Because our screen gutter is 4 units/16px on either side, the content col is 8 units wider than the grid.
 * We focus on container size, so we can roughly associate container breakpoints with screen sizes (and
 * think in terms of those screen sizes for the content column when the sidebar is open).

 * Two examples with a screen using pageColumnWidth12:
 * - Screen is 1024px wide, so with gutters, the _grid_ is 992px wide. The max col width does not apply because we can't fit a 1040px grid.
 *   We want container query @5xl (1024px) to apply  this case, so we can cleanly transition at 1024px screen width.
 *   Therefore, since `@container` applies to the content-box, we have to apply the gutters to a child of the `@container`.
 * - Screen is 1440px wide, so with gutters, the _grid_ is at our max 1040px wide. However, the overall main content
 *   area will be 1440px wide. So, we have to apply a max width of 1078 to the _content container_ , so we can max out at
 *   our target 1040px grid width, plus the screen gutters. In this case, we can still use @5xl, even though the container is a bit bigger.

 * There's also a bunch of complexity with the scroll gutter width and where we want it to render in the screen, so, we need a bunch of
 * layers of containers to get this all right.
 */

import { cn } from "@/lib/utils.ts";
import { Slot } from "@radix-ui/react-slot";
import React, { useCallback, useEffect, useState } from "react";

/** standard horizontal padding for screens */
export const screenGutter = "px-4";
/** We generally prefer to apply our container query containers to major screen elements, mostly the header, content col, and sidebar. */
export const screenContainer = cn("@container contain-layout");

/*
Common grid col basd layouts.

Note that we do not express this with CSS grid, we use widths, because in large breakpoints, when the
page width is constrained (e.g. by the nav panel being open), we want layouts to try to maintain
their target size, instead of proportionately shrinking them with the right whitespace.

For N columns, the inner grid formula is: (N*18 + (N-1)*4) == N*22 - 4
The container formula is: N*22 + 4, since we're adding 4 units of padding to the left and right.
*/
const pageColumnWidth7 = cn("w-full md:max-w-158 md:mx-auto"); // (7*22 - 4)
const pageColumnWidth8 = cn("w-full md:max-w-180 md:mx-auto"); // (8*22 - 4)
const pageColumnWidth12 = cn("w-full lg:max-w-268 lg:mx-auto"); // (12*22 - 4)
// The wide tier, for screens that pair a side rail with a card grid (Settings,
// the connectors gallery): 12 cols minus the rail leaves cards too narrow.
const pageColumnWidth14 = cn("w-full lg:max-w-304 lg:mx-auto"); // (14*22 - 4)

/** Number of columns in our design system 12-col content grid to use. */
export const pageColumnClasses = {
  7: pageColumnWidth7,
  8: pageColumnWidth8,
  12: pageColumnWidth12,
  14: pageColumnWidth14,
} as const;

/**
 * Scrollbar gutters effectively add fake paddding to the element, which makes width calculations tricky.
 *
 * This and @insetScrollbarWidthInner are used to effectively make the scrollbar inset. We add extra width
 * for the scrollbar, and then to ensure it's visible we translate the parent over by that amount then
 * the child back in by the same amount.
 *
 * For --scrollbar-thin-width, @see useProvideScrollbarWidth
 */
export const insetScrollbarWidthOuter = `w-[calc(100%+var(--scrollbar-thin-width))] -translate-x-[var(--scrollbar-thin-width)] overflow-x-clip`;

/** @see insetScrollbarWidthOuter */
export const insetScrollbarWidthInner = `translate-x-[var(--scrollbar-thin-width)]`;

/** Hide the native scrollbar (cross-browser) while keeping the element scrollable.
 *  Pair with `useFadeScrollbar` + `FadeScrollbarThumb` for a custom fading thumb. */
export const hideNativeScrollbarClass = "scrollbar-none";

type FadeThumb = { height: number; top: number; active: boolean };

/**
 * Drives a custom, fading scrollbar thumb for narrow embedded surfaces (the
 * calendar thread panel/list). Native scrollbar transitions aren't honored by
 * Chrome, so instead of styling the real bar we hide it (`hideNativeScrollbarClass`)
 * and render our own `FadeScrollbarThumb`, whose `opacity` *does* transition.
 *
 * Returns a callback ref for the scroll viewport, the resolved node (handy as an
 * IntersectionObserver root), and the thumb geometry/visibility. The thumb is a
 * non-interactive indicator — scrolling is via wheel/trackpad, not dragging it.
 */
export function useFadeScrollbar(enabled = true) {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [thumb, setThumb] = useState<FadeThumb | null>(null);
  useEffect(() => {
    if (!enabled || !node) {
      setThumb(null);
      return;
    }
    let idleTimeout: ReturnType<typeof setTimeout> | undefined;
    let raf = 0;
    let active = false;
    const measure = (): { height: number; top: number } | null => {
      const { scrollHeight, clientHeight, scrollTop } = node;
      if (scrollHeight <= clientHeight + 1) return null;
      const ratio = clientHeight / scrollHeight;
      const height = Math.max(24, Math.round(clientHeight * ratio));
      const top = Math.min(clientHeight - height, Math.max(0, Math.round(scrollTop * ratio)));
      return { height, top };
    };
    const sync = () => {
      const m = measure();
      setThumb(m ? { ...m, active } : null);
    };
    const scheduleSync = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        sync();
      });
    };
    const onScroll = () => {
      active = true;
      scheduleSync();
      if (idleTimeout) clearTimeout(idleTimeout);
      idleTimeout = setTimeout(() => {
        active = false;
        sync();
      }, 800);
    };
    sync();
    node.addEventListener("scroll", onScroll, { passive: true });
    const resizeObserver = new ResizeObserver(scheduleSync);
    resizeObserver.observe(node);
    return () => {
      node.removeEventListener("scroll", onScroll);
      resizeObserver.disconnect();
      if (idleTimeout) clearTimeout(idleTimeout);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [enabled, node]);
  return { setNode, node, thumb };
}

/**
 * Visual-only scrollbar thumb for `useFadeScrollbar`. Place inside a `relative`
 * ancestor that matches the scroll viewport's box; it pins to the right edge,
 * tracks scroll position, and fades via `opacity` (quick in, slow out).
 */
export const FadeScrollbarThumb = ({ thumb }: { thumb: FadeThumb | null }) => (
  <div
    aria-hidden
    className={cn(
      "pointer-events-none absolute right-0.5 top-0 z-10 w-1.5 rounded-full bg-muted-foreground/60",
      "transition-opacity ease-out",
      thumb?.active ? "opacity-100 duration-150" : "opacity-0 duration-700",
    )}
    style={
      thumb ? { height: thumb.height, transform: `translateY(${thumb.top}px)` } : { height: 0 }
    }
  />
);

interface ScreenOuterScrollWrapperProps extends React.ComponentPropsWithRef<"div"> {
  headerContent?: React.ReactNode;
  /**
   * `"scroll"` (default) always reserves the scrollbar gutter via the inset
   * trick — the right behavior for full screens where a disappearing scrollbar
   * would shift layout / break container queries. `"auto"` only renders a
   * scrollbar when the content actually overflows and drops the inset offset,
   * for narrow embedded surfaces (e.g. the calendar thread panel) where the
   * always-reserved gutter reads as a "scrollbar for no reason".
   */
  overflowMode?: "scroll" | "auto";
}

/**
 * Outer wrapper for a scrollable screen. Defines the actual scroll container and adjusts the width
 * to make the scrollbar visually "inset" (ScreenInnerScrollWrapper then sets the actual intended screen gutter).
 * Otherwise the scrollbar gutter width messes with our layout and container queries.
 *
 * The scrollbar is managed at this level so it won't be constrained to the content col's width. Otherwise,
 * the scrollbar would show up in the middle of the page on larger screens.
 *
 * Contains two layers - the first just sets up a relative container that fills the available
 * space, and the inner is an absolutely positioned element which has the overflow scroll behavior.
 *
 * @see ScreenInnerScrollWrapper
 */
export const ScreenOuterScrollWrapper = ({
  ref,
  children,
  className,
  headerContent,
  overflowMode = "scroll",
  ...props
}: ScreenOuterScrollWrapperProps) => {
  // `auto` mode hides the native bar and renders a custom fading thumb (see
  // `useFadeScrollbar`). Merge its setter with the forwarded ref so the element
  // drives both the consumer's scroll logic and the thumb geometry.
  const isAuto = overflowMode === "auto";
  const { setNode, thumb } = useFadeScrollbar(isAuto);
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      setNode(node);
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref, setNode],
  );

  return (
    <div className="flex-1 flex-col relative">
      <div
        className={cn(
          "absolute left-0 right-0 h-full",
          "isolate",
          "flex flex-col",
          // `auto` uses overflow-y-auto (vs. the always-reserving overflow-y-scroll)
          // and hides the native bar — the custom thumb below shows only mid-scroll,
          // so nothing is painted at rest even when the active-message spacer overflows.
          isAuto
            ? cn("overflow-y-auto overflow-x-clip", hideNativeScrollbarClass)
            : cn("overflow-y-scroll scrollbar-thin", insetScrollbarWidthOuter),
          className,
        )}
        ref={setRefs}
        {...props}
      >
        {headerContent}
        {children}
      </div>
      {isAuto && <FadeScrollbarThumb thumb={thumb} />}
    </div>
  );
};

/**
 * Inner wrapper for a scrollable screen. Defines the scrollable content.
 *
 * Contains two layers - the outer layer defines the width of the scrollable content, and therefore
 * the `@container`. The inner wrapper adds the standard screen gutter, which needs to be inside
 * the `@container` to avoid the padding breaking our container queries.
 *
 * This will only work correctly within ScreenOuterScrollWrapper, which ensures this content is full
 * width even if ScreenOuterScrollWrapper renders a scrollbar.
 *
 * @see ScreenOuterScrollWrapper
 */
export const ScreenInnerScrollWrapper = ({
  ref,
  children,
  containerClassName,
  className,
  asChild = false,
  insetScrollbar = true,
  ...props
}: React.ComponentPropsWithRef<"div"> & {
  containerClassName?: string;
  asChild?: boolean;
  /** Must match the outer wrapper's overflow mode — `false` drops the paired
   *  inner offset when the outer wrapper uses `overflowMode="auto"`. */
  insetScrollbar?: boolean;
}) => {
  const Component = asChild ? Slot : "div";
  return (
    <section
      className={cn(
        "flex flex-col flex-1",
        insetScrollbar && insetScrollbarWidthInner,
        screenContainer,
        containerClassName,
      )}
    >
      <Component
        className={cn("flex flex-col flex-1", screenGutter, className)}
        {...props}
        ref={ref}
      >
        {children}
      </Component>
    </section>
  );
};
